import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import ReportDetailsHeader from 'Components/ReportDetailsHeader';
import apiGlobal from 'global/api.global';
import { Button, Card, CardBody, CardFooter, CardHeader, Col, Form, Input, Label, Row } from 'reactstrap';
import { DO_ENGG_ReportingStatus, VesselTypeConstant, FuelTypes } from 'shared/constants';
import { RootState } from 'index';
import { useSelector } from 'react-redux';
import { getAuthData, loggedInUser } from 'utils/auth/authUtils';
import { useFormik } from 'formik';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { queryClient } from 'react-query/queryClient';
import {
    dateTimeFormat,
    handleDeckRemarksPreviousButton,
    handleServerResponse,
    saveEditForm,
    useSafeFormikContext
} from 'GenericForms/Helper';
import { useDocumentTitle } from 'Components/useDocument.hooks';
import FormLoading from 'Components/FormLoading';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';
import ErrorComponent from 'Components/ErrorComponent';
import env from 'environment_system/env_system';

interface RemarksType {
    handleVesselState: any,
    toggleTab: any,
    setErrorMessage: any,
    toggleAddLfondsTab: any,
}

const DeckRemarks = ({
    handleVesselState,
    toggleTab,
    setErrorMessage,
    toggleAddLfondsTab,
}: RemarksType) => {
    useDocumentTitle('Deck Officer Remarks - ecoSAIL');
    const { editReportBool, toggle, activeEditTab, previousDeckTabIndex, setPreviousDeckTabIndex, tabsIdListDeck } = useEditReport();

    const { ReportID, VesselID, VoyageID, Vessels, CurrentReport, ConfigurationLimits } = useSelector((state: RootState) => state.Reporting);
    const record = CurrentReport?.data?.vessel_reporting_information ?? null;
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const vessel = Vessels.find((rec: any) => rec.id === VesselID);
    const isLNG = vessel.vessel_type_precedence_id === VesselTypeConstant.LNG_CARRIER && vessel.display === true ? true : false;
    const FuelType = ConfigurationLimits?.vessel_fuel_information || [];
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;

    const filteredOptions = FuelType?.filter(
        (item: any) => item?.fuel_sub_type_precedence_id !== FuelTypes.LNG_CARGO
    );

    useEffect(() => {
        setPreviousDeckTabIndex(tabsIdListDeck?.length - 1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /** Fetch Remarks */
    const {
        data: DeckOfficerRemark,
        isLoading: DeckOfficerRemarkLoading,
        isError: DeckOfficerRemarkError,
    }: { data: any[]; isLoading: any, isError: any } = useQuery(
        [queryKeyes.vessel.DeckOfficerRemark.key, VesselID, ReportID],
        async () => {
            return await Get(queryKeyes.vessel.DeckOfficerRemark.url(VesselID, ReportID), VesselID, ReportID);
        },
        {
            enabled: true,
            staleTime: 0,
            onSuccess: () => setRefreshKey(prev => prev + 1),
        }
    );

    const {
        data: PilotDetails,
        isLoading: PilotDetailsLoading,
        isError: PilotDetailsError,
    }: { data: any[]; isLoading: any, isError: any } = useQuery(
        [queryKeyes.vessel.PilotDetails.key, VesselID, ReportID],
        async () => {
            return await Get(queryKeyes.vessel.PilotDetails.url(VesselID, ReportID), VesselID, ReportID);
        },
        {
            enabled: true,
            staleTime: 0,
            onSuccess: () => setRefreshKey(prev => prev + 1),
        }
    );

    /** Save logic */
    const saveRemark = async (remark: any) => {
        const responseArray: any = [];
        const remarkData = {
            ...remark
        };
        responseArray.push(apiGlobal.post(`/deck_remark_and_pilot_details/`, remarkData));
        await handleServerResponse(responseArray).then(async (res) => {
            if (res === true) {
                setErrorMessage(null);
                await queryClient.invalidateQueries(queryKeyes.vessel.DeckOfficerRemark.key);
                await setRefreshKey(refreshKey + 1);
            } else {
                setErrorMessage(res);
            }
        });
    };

    const handleSaveandComplete = async (values: any) => {
        const responseArray: any = [];
        await saveRemark(values);
        responseArray.push(apiGlobal.patch(`/vessel_reporting_information/${ReportID}/`, {
            officer_reporting_status: DO_ENGG_ReportingStatus.COMPLETE
        }));
        await handleServerResponse(responseArray).then(async (res) => {
            if (res === true) {
                setErrorMessage(null);
                queryClient.invalidateQueries(queryKeyes.vessel.DeckOfficerRemark.key);
                queryClient.invalidateQueries(queryKeyes.vessel.PilotDetails.key);
                handleVesselState('ONGOING_VESSEL_REPORTING');
            } else {
                setErrorMessage(res);
            }
        });
    };

    /** Helper to generate ROB initial values */
    const getInitialROBValues = (filteredOptions: any) => {
        return filteredOptions?.reduce((acc: any, fuel: any) => {
            acc[fuel.fuel_type_precedence_id] = {
                fuel_type_name: fuel.fuel_type_name,
                vessel_fuel: fuel.id,
                rob: '',
            };
            return acc;
        }, {});
    };

    /** Initial values */
    const getDeckRemarks = () => {
        const base: any = {
            remarks: {
                remarks: "",
                user: loggedInUser(),
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
            },
            pilot_details: {
                is_pilot_details_available: false,
                pilot_embark_date_time: null,
                pilot_disembark_date_time: null,
                start_rob: getInitialROBValues(filteredOptions),
                end_rob: getInitialROBValues(filteredOptions),
                vessel: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
            },
        };

        if (editReportBool && editInitialValues?.deck_officer?.deck_officer_remarks) {
            return {
                remarks: editInitialValues.deck_officer.deck_officer_remarks,
                pilot_details: editInitialValues.deck_officer.pilot_details,
            };
        } else if (DeckOfficerRemark?.length > 0 && PilotDetails?.length > 0) {
            return {
                remarks: DeckOfficerRemark[0],
                pilot_details: PilotDetails[0],
            };
        } else if (DeckOfficerRemark?.length === 0 && PilotDetails?.length === 0) {
            return base;
        } else if (DeckOfficerRemark?.length === 0) {
            return {
                remarks: base.remarks,
                pilot_details: PilotDetails?.[0],
            };
        } else if (PilotDetails?.length === 0) {
            return {
                remarks: DeckOfficerRemark?.[0],
                pilot_details: base.pilot_details,
            }
        } else {
            return base;
        }
    };

    /** Formik setup */
    const DeckOfficerRemarksFormik = useFormik({
        enableReinitialize: true,
        initialValues: getDeckRemarks(),
        onSubmit: async (values: any, actions: any) => {
            actions.setSubmitting(true);
            if (values.remarks.remarks) {
                await saveRemark(values);
            }
            actions.setSubmitting(false);
        }
    });

    useEffect(() => {
        DeckOfficerRemarksFormik.initialValues = getDeckRemarks();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DeckOfficerRemark]);

    useEffect(() => {
        if (editValues && editValues?.deck_officer?.deck_officer_remarks) {
            setEditValues({
                ...editValues,
                deck_officer: {
                    ...editValues.deck_officer,
                    deck_officer_remarks: {
                        ...DeckOfficerRemarksFormik.values.remarks,
                        report_index: null,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                        user: getAuthData('user_id'),
                        vessel: VesselID
                    },
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DeckOfficerRemarksFormik?.values]);

    return (
        <Card>
            <CardHeader className='p-2 pb-0'>
                <div className="text-center mb-3">
                    <Row>
                        <Col>
                            <h5 className="page_title pos-start mb-0">Remarks (if any)</h5>
                        </Col>
                        <Col>
                            <ReportDetailsHeader />
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {(DeckOfficerRemarkError || PilotDetailsError) && <ErrorComponent message={'Unable to load required data!'} />}
            {(DeckOfficerRemarkLoading || PilotDetailsLoading) && (<FormLoading />)}
            {(!DeckOfficerRemarkLoading || !PilotDetailsLoading) && (!DeckOfficerRemarkError || PilotDetailsError) && (
                <Form
                    className="needs-validation"
                    autoComplete="off"
                    onSubmit={DeckOfficerRemarksFormik.handleSubmit}
                    key={refreshKey}
                >
                    <CardBody className='deck-card-body pt-2'>
                        {/* Remarks Section */}
                        <Row>
                            <Col>
                                <ReactQuill
                                    key={refreshKey}
                                    theme="snow"
                                    value={DeckOfficerRemarksFormik?.values?.remarks?.remarks}
                                    onChange={(value: any) => {
                                        DeckOfficerRemarksFormik.setFieldValue('remarks.remarks', value);
                                    }}
                                    modules={{
                                        toolbar: [
                                            ['bold', 'italic', 'underline', 'strike'],
                                            [{ list: 'ordered' }, { list: 'bullet' }],
                                            [{ align: [] }],
                                            ['clean']
                                        ]
                                    }}
                                    className="custom-quill-editor table-light"
                                />
                            </Col>
                        </Row>
                        {env.inDevelopment && (
                            <Row className="mt-4">
                                <Col>
                                    <h5>Pilot Details</h5>
                                    <Label className="form-label">Is pilot details available?</Label>
                                    <div className="square-switch mt-1">
                                        <Input
                                            type="checkbox"
                                            switch="none"
                                            name="pilot_details.is_pilot_details_available"
                                            id="pilot_details_switch"
                                            checked={DeckOfficerRemarksFormik?.values?.pilot_details?.is_pilot_details_available}
                                            onChange={DeckOfficerRemarksFormik?.handleChange}
                                            onClick={(e: any) => {
                                                const checked = e.target.checked;
                                                // Update checkbox value in formik
                                                DeckOfficerRemarksFormik.setFieldValue(
                                                    "pilot_details.is_pilot_details_available",
                                                    checked
                                                );
                                                // If unchecked → reset to initial values
                                                if (!checked) {
                                                    DeckOfficerRemarksFormik.setFieldValue(
                                                        "pilot_details",
                                                        getDeckRemarks().pilot_details
                                                    );
                                                }
                                            }}
                                        />
                                        <Label
                                            htmlFor="pilot_details_switch"
                                            data-on-label="Yes"
                                            data-off-label="No"
                                            className="mt-1"
                                            tabIndex={0}
                                        />
                                    </div>
                                    <div className="table-responsive mt-2">
                                        <table className="table mt-2 align-middle">
                                            <thead className="table-light">
                                                <tr>
                                                    <th style={{ width: '20%' }}>Duration</th>
                                                    <th style={{ width: '25%' }}>Date & Time (Local)</th>
                                                    {filteredOptions?.map((fuel: any) => (
                                                        <th key={fuel?.fuel_type_precedence_id} className="text-center">
                                                            {fuel?.fuel_type_name}
                                                        </th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>Pilot embark</td>
                                                    <td>
                                                        <Input
                                                            type="datetime-local"
                                                            name="pilot_details.pilot_embark_date_time"
                                                            id="pilot_embark_date_time"
                                                            className="datetimepicker text-uppercase"
                                                            value={
                                                                DeckOfficerRemarksFormik?.values?.pilot_details?.is_pilot_details_available &&
                                                                dateTimeFormat(DeckOfficerRemarksFormik.values?.pilot_details?.pilot_embark_date_time)}
                                                            onChange={DeckOfficerRemarksFormik.handleChange}
                                                            disabled={DeckOfficerRemarksFormik.values?.pilot_details?.is_pilot_details_available === false}
                                                        />
                                                    </td>
                                                    {filteredOptions?.map((fuel: any) => {
                                                        const fieldName = `pilot_details.start_rob.${fuel.fuel_type_precedence_id}.rob`;
                                                        const fieldId = `pilot_start_rob_${fuel.fuel_type_precedence_id}`;
                                                        return (
                                                            <td key={fieldId}>
                                                                <Input
                                                                    type="text"
                                                                    name={fieldName}
                                                                    id={fieldId}
                                                                    value={
                                                                        DeckOfficerRemarksFormik.values?.pilot_details?.is_pilot_details_available
                                                                            ? String(
                                                                                DeckOfficerRemarksFormik.values?.pilot_details?.start_rob?.[
                                                                                    fuel.fuel_type_precedence_id
                                                                                ]?.rob ?? ""
                                                                            )
                                                                            : ""
                                                                    }
                                                                    onChange={DeckOfficerRemarksFormik.handleChange}
                                                                    onBlur={DeckOfficerRemarksFormik.handleBlur}
                                                                    placeholder="ROB"
                                                                    disabled={DeckOfficerRemarksFormik.values?.pilot_details?.is_pilot_details_available === false}
                                                                />
                                                            </td>
                                                        );
                                                    })}
                                                </tr>

                                                <tr>
                                                    <td>Pilot disembark</td>
                                                    <td>
                                                        <Input
                                                            type="datetime-local"
                                                            name="pilot_details.pilot_disembark_date_time"
                                                            id="pilot_disembark_date_time"
                                                            className="datetimepicker text-uppercase"
                                                            value={
                                                                DeckOfficerRemarksFormik.values?.pilot_details?.is_pilot_details_available &&
                                                                dateTimeFormat(DeckOfficerRemarksFormik.values.pilot_details.pilot_disembark_date_time)
                                                            }
                                                            onChange={DeckOfficerRemarksFormik.handleChange}
                                                            disabled={DeckOfficerRemarksFormik.values?.pilot_details?.is_pilot_details_available === false}
                                                        />
                                                    </td>
                                                    {filteredOptions?.map((fuel: any) => {
                                                        const fieldName = `pilot_details.end_rob.${fuel.fuel_type_precedence_id}.rob`;
                                                        const fieldId = `pilot_end_rob_${fuel.fuel_type_precedence_id}`;
                                                        return (
                                                            <td key={fieldId}>
                                                                <Input
                                                                    type="text"
                                                                    name={fieldName}
                                                                    id={fieldId}
                                                                    value={
                                                                        DeckOfficerRemarksFormik.values?.pilot_details?.is_pilot_details_available
                                                                            ? String(
                                                                                DeckOfficerRemarksFormik.values?.pilot_details?.end_rob?.[
                                                                                    fuel.fuel_type_precedence_id
                                                                                ]?.rob ?? ""
                                                                            )
                                                                            : ""
                                                                    }
                                                                    onChange={DeckOfficerRemarksFormik.handleChange}
                                                                    onBlur={DeckOfficerRemarksFormik.handleBlur}
                                                                    placeholder="ROB"
                                                                    disabled={DeckOfficerRemarksFormik.values?.pilot_details?.is_pilot_details_available === false}
                                                                />
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        )}
                    </CardBody>
                    <CardFooter className="p-2 py-3">
                        <Row className="d-flex align-items-center flex-row-reverse">
                            <Col lg={10}>
                                <div className="d-flex flex-wrap gap-3 justify_right">
                                    {!editReportBool ? (
                                        <>
                                            {isLNG && (
                                                <Button type="submit" color="primary" className="btn_size_cstm">
                                                    Save
                                                </Button>
                                            )}
                                            <Button
                                                type="button"
                                                color="primary"
                                                className="btn_size_cstm"
                                                onClick={() => handleSaveandComplete(DeckOfficerRemarksFormik.values)}
                                            >
                                                {isLNG ? "Save & Complete" : "Complete"}
                                            </Button>
                                        </>
                                    ) : (
                                        <Button
                                            type="button"
                                            color="primary"
                                            className="btn_size_cstm"
                                            disabled={DeckOfficerRemarksFormik.isSubmitting || editIsSubmitting}
                                            onClick={async () => {
                                                if (editReportBool && DeckOfficerRemarksFormik.isValid) {
                                                    saveEditForm(editSubmit, toggle, activeEditTab);
                                                }
                                            }}
                                        >
                                            {(DeckOfficerRemarksFormik.isSubmitting || editIsSubmitting) && (
                                                <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                            )}
                                            Next <i className="bx bx-chevron-right ms-1" />
                                        </Button>
                                    )}
                                </div>
                            </Col>
                            <Col lg={2}>
                                <Button
                                    type="button"
                                    color="primary"
                                    className="btn_size_cstm"
                                    onClick={() => {
                                        handleDeckRemarksPreviousButton(record, toggleTab, previousDeckTabIndex, toggleAddLfondsTab);
                                        setErrorMessage(null);
                                        DeckOfficerRemarksFormik.setErrors({});
                                    }}
                                >
                                    <i className="bx bx-chevron-left me-1" /> Previous
                                </Button>
                            </Col>
                        </Row>
                    </CardFooter>
                    <FormValuesDebug values={[DeckOfficerRemarksFormik.values]} />
                </Form>
            )}
        </Card>
    );
};

export default DeckRemarks;
